import { Coordinate, LightsContainer, ScreenDimensions, TileSize, Visibility } from "game-common/models"
import {
    getLightPolygon,
    getSightPolygon,
    pruneSegments,
    SightPoint,
    VectorLine,
    WallFaceReveal,
} from "game-common/ray_casting"
import {
    Application, BLEND_MODES, Container, Filter, filters, Graphics, Matrix, Rectangle, Renderer, RenderTexture, SCALE_MODES, Sprite,
    Texture, utils,
} from "pixi.js"
import { ClientGameLogic } from "../../../client_game_logic"
import { logger } from "../../../logger"
import { TranslationComputer } from "./translation_computer"
import { SCALER, ScalingParams } from "../../../client_util"
import { isExteriorFloorFall } from "game-common/floor_fall"
import { VisibilityFade } from "./visibility_fade"
import { INDOOR_SIGHT, indoorSightConfig, penumbraFan, sightReachesTile } from "./indoor_sight"
import { NightLook, nightLookFor, NightSkyFilter } from "./night_sky"
import { bodyLiftAt, hillLiftOf } from "./hill_lift"

interface VisionOptions {
    polyMap: Array<VectorLine>
    location: Coordinate
    debug?: Graphics
    visibility?: Visibility
    lights?: LightsContainer
    /** The sprites drawing the tile at (x, y) -- a wall's art, its top included -- in the masked surface's space. */
    wallArt?: (x: number, y: number) => Sprite[]
    /**
     * A wall sprite's texture before damage cut holes in it. The veil is lifted off that, the wall's whole silhouette: a
     * hole left the veil standing in it, and the veil darkens everything drawn there, so whoever stood in front of the
     * wall wore every hole behind them as a dark blot.
     */
    intactArt?: (art: Sprite) => Texture
    /** How much of the sky the rain's clouds cover (0..1): what hides the moon by night (night_sky.ts). */
    cloud?: number
}

// How far a polygon edge may sit inside the true circle it stands in for. A light's boundary used
// to be a fixed 64-gon whatever its size, which is 64 ray targets -- more than every occluder
// around it put together -- to hold a 0.4px error at a torch's radius. Deriving the count from the
// radius instead holds the error where it stops being visible and costs a fraction of the rays.
const CircleFlatness = 0.75

// Never fewer than this, so a tiny light still reads as round, nor more than the old fixed count.
const MinCircleSides = 16
const MaxCircleSides = 64

const circleSides = (radius: number) => {
    if (!(radius > CircleFlatness)) return MinCircleSides
    const sides = Math.ceil(Math.PI / Math.acos(1 - CircleFlatness / radius))
    return Math.min(MaxCircleSides, Math.max(MinCircleSides, sides))
}

/**
 * The light map laid over the world rather than masking it: a blend pixi does not define, the world's colour multiplied
 * by the map's (grey: white light, black veil), its alpha left alone. A sprite mask did the same sum the long way round --
 * the whole world drawn into a texture of its own, then drawn again through the mask, every frame whenever it was not
 * full day -- and the stage is cleared to black, so what the mask hid showed black just as the multiply leaves it.
 */
const LIGHT_MAP_BLEND = 40 as BLEND_MODES

const ensureLightMapBlend = (renderer: Renderer): void => {
    const state = renderer.state as unknown as { blendModes?: number[][] } | undefined
    const gl = renderer.gl
    // No real renderer (tests): nothing to draw with.
    if (!state?.blendModes || !gl) return
    // Rebuilt by pixi on a restored context: set again whenever it is missing.
    if (!state.blendModes[LIGHT_MAP_BLEND]) state.blendModes[LIGHT_MAP_BLEND] = [gl.ZERO, gl.SRC_COLOR, gl.ZERO, gl.ONE]
    // The batcher looks a sprite's blend up by its texture's alpha mode first.
    for (const table of utils.premultiplyBlendMode) table[LIGHT_MAP_BLEND] = LIGHT_MAP_BLEND
}

// The outdoor light gradient's softening. See the constructor for why it is dialled down.
const BlurRadius = 50
const BlurQuality = 2
const BlurResolution = 0.25

const softBlur = () => {
    const blur = new filters.BlurFilter(BlurRadius, BlurQuality, BlurResolution)
    return blur
}

// What ground out of sight keeps of its light: next to nothing. The blurred path's floor, which is
// the faintest fill it can draw (a fill at alpha 0 is not drawn at all).
const HiddenBrightness = 0.01

// The veil texture overhangs the mask by this much on every side, so its feather blurs in veil from
// beyond the screen's edge instead of fading out at it and ringing the screen with a lit border.
const VeilOverhang = TileSize

// How far around the eye to look for walls sight reaches, in tiles. The server prunes a building's
// occluders to seven tiles around the player, so nothing further out stops a ray anyway.
const WallSearchTiles = 8

// drawPolygon's other overload takes Point instances, and flattens them itself. Handing it the
// flat form skips that copy, and is what the sight polygon's vertices are closest to anyway.
const flatten = (polygon: ReadonlyArray<{ x: number; y: number }>): number[] => {
    const flat = new Array<number>(polygon.length * 2)
    for (let i = 0; i < polygon.length; i++) {
        flat[i * 2] = polygon[i].x
        flat[i * 2 + 1] = polygon[i].y
    }
    return flat
}

/** Even-odd point-in-polygon. */
const insidePolygon = (polygon: ReadonlyArray<{ x: number; y: number }>, x: number, y: number): boolean => {
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i], b = polygon[j]
        if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside
    }
    return inside
}

/**
 * A light polygon cut down to `radius`: every vertex lies on its own ray from the light, so pulling the far
 * ones in along that ray is the same shape the light would have had with the shorter reach.
 */
const clampToRadius = (polygon: ReadonlyArray<{ x: number; y: number }>, x: number, y: number, radius: number) =>
    polygon.map(p => {
        const d = Math.hypot(p.x - x, p.y - y)
        return d <= radius ? p : { x: x + (p.x - x) * radius / d, y: y + (p.y - y) * radius / d }
    })

export class ShadowRendering {
    private lastLocation: Coordinate = { x: -1, y: -1 }
    private visibilityTexture: Texture
    private visibilitySprite: Sprite
    private visibilityBounds: Rectangle
    private visibilitySurface: Graphics
    private softLightFilters: Filter[]
    private visibilityLastUpdateTs?: number
    private app: Application
    private surface: Container
    private cleared: boolean = true
    private lightLastChangedTs?: number
    private latestDarkness: number
    private lastCameraTransform = ""
    private logic: ClientGameLogic
    private roomId: string
    private translationComputer = new TranslationComputer()
    private outgoingSight: SightPoint[] | null = null
    private occlusionFade = new VisibilityFade()
    private latestOcclusionStrength = 0

    // The mask is baked into ONE render texture, reused for the life of the room.
    //
    // There used to be a Map of up to ten textures keyed by this hash, with a fresh
    // renderer.generateTexture() -- a new GPU texture allocation -- on every miss and a FIFO
    // destroy behind it. It could not hit: the hash carries the player's position at full
    // precision and the stage offset, so every frame the player moves is a unique key, and when
    // the player stands still the hash-unchanged check below returns before the cache is ever
    // consulted. So it allocated and freed a texture per moving frame and served nothing, which
    // on a weak GPU is exactly the alloc/free churn that stalls the driver.
    private needsTextureUpdate: boolean = false
    private cachedVisibilityHash: string = ""
    private visibilityRenderTexture?: RenderTexture
    private bakedWidth = 0
    private bakedHeight = 0
    // Reused for the bake's world->texture translation, to match what generateTexture did.
    private bakeTransform = new Matrix()
    // A building's sightline, baked over the lights rather than blurred in with them. The light
    // blur is 50px: right for a torch's falloff, but on a wall it let the room's light seep about
    // a tile through every side and blurred the very line the occlusion exists to draw. Its few
    // wide taps also stacked faint copies of that edge 25px apart, which banded the floor.
    //
    // The veil is baked into its own texture first: black over everything, then ERASED where the
    // eye can see -- the sight polygon, a soft fan off each ray's edge (see penumbraFan), and the
    // art of every wall sight reaches. Sight is cast along the walls' footprints, but a wall is
    // drawn standing up off its footprint, so the polygon alone cut the top off every wall and
    // halved every post. Erasing the wall sprites themselves shows exactly what is drawn, of any
    // material, without a pixel of the ground behind it.
    private sightVeil: Graphics
    private sightErase: Graphics
    private wallErase = new Container()
    private wallProxies: Sprite[] = []
    private veilTexture?: RenderTexture
    private veilSprite = new Sprite()
    private veilTransform = new Matrix()
    private veilFeather?: InstanceType<typeof filters.BlurFilter>
    private veilActive = false
    private penumbra: number[] = []
    private lastSightKey = ""
    // Outdoors by night the world is drawn through the light map by this filter rather than multiplied by the sprite
    // (night_sky.ts): `look` is this frame's, undefined where the plain darkness stays.
    private nightFilter = new NightSkyFilter()
    private look?: NightLook
    public debugHashCalculation: boolean = false // Debug flag for hash calculation logging

    constructor(app: Application, surface: Container, debug: boolean, logic: ClientGameLogic) {
        this.app = app
        this.surface = surface
        this.logic = logic

        const visibilitySurface = new Graphics()
        // A 50px blur at full resolution and the default four passes is a lot of fill for an
        // effect whose whole job is to be indistinct. Half the passes at a quarter of the
        // resolution is ~8-16x less work and, at this radius, not tellable apart -- the blur is
        // wider than the error it introduces by an order of magnitude. This runs on every rebake,
        // so it is a per-frame cost while the player walks.
        this.softLightFilters = debug ? [] : [softBlur()]
        visibilitySurface.filters = this.softLightFilters
        visibilitySurface.scale.set(ScalingParams.surfaceZoom)
        this.visibilitySurface = visibilitySurface

        this.sightVeil = new Graphics()
        this.sightErase = new Graphics()
        this.sightErase.blendMode = BLEND_MODES.ERASE
        for (const layer of [this.sightVeil, this.sightErase, this.wallErase]) layer.scale.set(ScalingParams.surfaceZoom)
        if (!debug) {
            this.veilFeather = new filters.BlurFilter(INDOOR_SIGHT.feather, 2)
            // Fitted to the mask, the blur would cut the veil's overhang off before blurring it.
            this.veilFeather.autoFit = false
        }
        this.translationComputer.noDelay = true
    }

    // Shared with the cave minimap; generated once by the world visibility pass.
    sightPolygon: Coordinate[] | null = null
    sightRoomId: string | undefined

    /** The light map laid over the world, while there is one (it multiplies the world: LIGHT_MAP_BLEND). */
    get lightMap(): Sprite | undefined {
        return this.visibilitySprite?.parent ? this.visibilitySprite : undefined
    }

    renderVisibility = (visionOptions: VisionOptions): boolean => {
        this.look = nightLookFor(visionOptions.visibility, !!this.logic.dayPhase, visionOptions.cloud)
        const shouldRender = this.prepare(visionOptions)
        if (!shouldRender) {
            // A mask still shown keeps its look; a cleared one drops it.
            this.applyNightLook()
            return false
        }

        // NEW: Only render if we need to update the texture
        if (this.needsTextureUpdate) {
            this.render()
            this.needsTextureUpdate = false
        }
        this.applyNightLook()

        return true
    }

    /** Draw the world through the light map (the night look) or under it (the sprite's multiply), whichever is due. */
    private applyNightLook = () => {
        const map = this.lightMap
        const filters = this.surface.filters ?? []
        const applied = filters.includes(this.nightFilter)
        if (this.look && map) {
            this.nightFilter.set(this.look, map)
            // At the screen's own resolution: at pixi's default of 1 the world's pixel art would be resampled.
            this.nightFilter.resolution = this.app.renderer.resolution
            map.renderable = false
            if (!applied) {
                this.surface.filters = [...filters, this.nightFilter]
                // The whole screen: the world's bounds are a thousand children to measure every frame.
                this.surface.filterArea = this.app.screen
            }
            return
        }
        if (map) map.renderable = true
        if (applied) {
            const rest = filters.filter(filter => filter !== this.nightFilter)
            this.surface.filters = rest.length ? rest : null
            this.surface.filterArea = null
        }
    }

    private prepare = ({ polyMap, location, visibility, debug, lights, wallArt, intactArt }: VisionOptions): boolean => {
        if (!this.logic.tilemapMetaProvider) {
            return false
        }
        // The logical body position can overlap each eave while the actual body
        // falls outside the facade. Do not restart indoor sight at every storey.
        const exteriorFall = !visibility?.fullOcclusion && isExteriorFloorFall(this.logic.playerEntity?.floorFall)
        if (exteriorFall) polyMap = undefined
        // The night look lightens the floor (night_sky.ts): the light map is baked at its level instead.
        const darkness = this.look?.level ?? visibility?.darkness
        const hasDarkness = darkness < 1.0
        const hasPolymap = polyMap?.length > 0
        // Caves took this blur away because blurring the dense polygon mask produced a nearly
        // empty render texture on the live WebGL client. That was the NaN vertices a vertical
        // ray used to emit (see getIntersection): they made the Graphics bounds non-finite, so
        // Pixi computed an empty filter area. With finite bounds the blur is safe everywhere.
        this.visibilitySurface.filters = this.softLightFilters

        if (this.roomId !== this.logic.tilemapMetaProvider?.roomId) {
            this.lastLocation = { x: -1, y: -1 }
            this.translationComputer.update(this.logic.tilemapMetaProvider)
            this.roomId = this.logic.tilemapMetaProvider.roomId
            this.outgoingSight = null
            this.occlusionFade.reset()
            // NEW: Clear cache on room change
            this.invalidateMask()
        }

        // Keep the last indoor sight polygon in world coordinates while the areas it hid
        // brighten. Recasting from outside would jump the sightline across the doorway.
        // Room changes and exterior falls must never carry an old building's mask along.
        if (exteriorFall || visibility?.fullOcclusion) {
            this.outgoingSight = null
            this.occlusionFade.reset()
        }
        this.occlusionFade.update(hasPolymap)
        const occlusionStrength = this.occlusionFade.alpha
        if (!hasPolymap && occlusionStrength === 0) this.outgoingSight = null
        const fadingOcclusion = !hasPolymap && occlusionStrength > 0
        const occlusionChanged = this.latestOcclusionStrength !== occlusionStrength || hasPolymap !== (this.sightPolygon !== null)
        // A building's walls are veiled over the lights (see drawSightVeil). A cave keeps its
        // sightline inside the light layer, where its wall-face reveal and rock-shaped lights
        // were tuned against the wide blur.
        const sight = indoorSightConfig()
        const veiled = sight.crisp && !visibility?.fullOcclusion && (hasPolymap || fadingOcclusion)
        const sightKey = `${sight.crisp}:${sight.feather}:${sight.penumbra}`
        const sightChanged = sightKey !== this.lastSightKey
        const backgroundAlpha = occlusionStrength === 1 ? HiddenBrightness : darkness + (HiddenBrightness - darkness) * occlusionStrength
        // Account for the background composited over the sight polygon. Both sides of
        // the boundary blend to outdoor lighting without pulsing already visible ground.
        const sightAlpha = occlusionStrength === 0 ? 0 : darkness * 0.99 * occlusionStrength / (1 - backgroundAlpha)

        if (!hasPolymap && !fadingOcclusion && !hasDarkness && this.cleared === false) {
            this.translationComputer.reset()
            this.visibilitySurface.clear()
            this.cleared = true
            this.surface.mask = undefined
            this.sightPolygon = null
            this.sightRoomId = this.logic.roomId
            if (this.visibilitySprite) {
                this.app.stage.removeChild(this.visibilitySprite)
            }
            this.lastLocation = { x: -1, y: -1 }
            // NEW: Clear cache when visibility is disabled
            this.invalidateMask()
            return false
        }

        const cameraTransform = `${this.surface.position.x}:${this.surface.position.y}:${this.surface.pivot.x}:${this.surface.pivot.y}`
        const cameraChanged = cameraTransform !== this.lastCameraTransform
        const lightsChanged = this.lightLastChangedTs !== lights?.lastUpdatedTs
        const playerLocationChanged = !(location.x === this.lastLocation.x && location.y === this.lastLocation.y)
        const darknessChanged = this.latestDarkness !== darkness
        // The mask is baked in stage coordinates. Include the newly exposed areas on all edges
        // and rebake after resizing even when the player and lights have not moved.
        const left = 0 - this.app.stage.x
        const top = 0 - this.app.stage.y / SCALER
        const viewportChanged = !this.visibilityBounds || this.visibilityBounds.x !== left || this.visibilityBounds.y !== top
        if (viewportChanged) {
            this.visibilityBounds = new Rectangle(
                left, top, 27 * TileSize + this.app.stage.x * 2, 16 * TileSize + this.app.stage.y * 2 / SCALER,
            )
        }
        if (this.visibilitySprite) this.visibilitySprite.position.set(left, top)

        // NEW: Calculate visibility hash to determine if update is needed
        const hashState = {
            polyMap,
            location,
            visibility,
            lights,
            playerLocationChanged,
            lightsChanged,
            darknessChanged,
        }

        if (this.debugHashCalculation) {
            this.debugHashCalculationLog(hashState)
        }

        const currentVisibilityHash = `${this.computeVisibilityHash(hashState)}-${occlusionStrength}-${sightKey}-${darkness}`

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "debug"] },
                `[Shadow Cache] Current hash: ${currentVisibilityHash}, Cached hash: ${this.cachedVisibilityHash}`,
            )
        }

        // Nothing that shapes the mask has changed, so the texture already on the sprite stands.
        if (currentVisibilityHash === this.cachedVisibilityHash) {
            this.needsTextureUpdate = false // Mark as cache hit
            if (this.debugHashCalculation) {
                logger.debug(
                    { tags: ["shadow", "cache", "hit"] },
                    `[Shadow Cache] Same hash, no update needed: ${currentVisibilityHash}`,
                )
            }
            return true
        }

        // Check if we should skip expensive operations based on old logic
        if (!hasPolymap && !fadingOcclusion) {
            // there isn't a poly map. In this case, then we don't care about player location changes,
            // only pure darkness changes
            if (
                !hasDarkness ||
                (this.translationComputer.ready() &&
                    !occlusionChanged && !cameraChanged && !viewportChanged && !darknessChanged && !playerLocationChanged && !lightsChanged)
            ) {
                return false
            }
        } else if (hasPolymap) {
            // there's a poly map. in this case we care about player changes
            if (!occlusionChanged && !cameraChanged && !viewportChanged && !playerLocationChanged && !lightsChanged && !sightChanged) {
                if (this.visibilityLastUpdateTs === visibility?.lastUpdateTs) {
                    return false
                }
            }
        }

        if (this.visibilityLastUpdateTs !== visibility?.lastUpdateTs) {
            this.visibilityLastUpdateTs = visibility?.lastUpdateTs
        }

        this.lightLastChangedTs = lights.lastUpdatedTs
        this.latestDarkness = darkness
        this.latestOcclusionStrength = occlusionStrength
        this.lastSightKey = sightKey
        this.sightPolygon = null
        this.sightRoomId = this.logic.roomId
        this.cleared = false
        this.veilActive = false

        const g = this.visibilitySurface

        g.clear()

        const playerLocation = { x: location.x, y: location.y }

        // Bake in the displayed world's transform, including a level pan, shake and pivot.
        // A second camera can disagree during transitions and expose a bright frame.
        g.position.set(this.surface.position.x, this.surface.position.y)
        g.pivot.copyFrom(this.surface.pivot)
        this.lastCameraTransform = cameraTransform

        if (hasPolymap) {
            const vs = visibility?.range || 600

            // Top
            polyMap[0] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y - vs },
            }
            // Bottom
            polyMap[1] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y + vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }
            // Left
            polyMap[2] = {
                a: { x: playerLocation.x - vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x - vs, y: playerLocation.y + vs },
            }
            // Right
            polyMap[3] = {
                a: { x: playerLocation.x + vs, y: playerLocation.y - vs },
                b: { x: playerLocation.x + vs, y: playerLocation.y + vs },
            }

            const prunedSegments = pruneSegments(polyMap, playerLocation.x, playerLocation.y, vs, 3)
            // A cave's occluders sit on the rock's true tile boundary, so the sightline stops
            // at the surface. Reveal the near half of whatever it stopped against, or the
            // player would stand in a black box with no visible walls.
            const reveal = visibility?.fullOcclusion ? WallFaceReveal : 0

            let polygons = [getSightPolygon(prunedSegments, playerLocation.x, playerLocation.y, reveal)]
            this.sightPolygon = polygons[0]
            this.outgoingSight = visibility?.fullOcclusion ? null : polygons[0]
            if (debug) {
                // debug.transform.position.x = -playerLocation.x + 50 + ScreenDimensions.w / 2
                // debug.transform.position.y = -playerLocation.y + 50 + ScreenDimensions.h / 2

                debug.transform.position.x =
                    (-playerLocation.x + 50 + ScreenDimensions.w / 2) * 2 - 32 * ScalingParams.viewportX
                debug.transform.position.y =
                    (-playerLocation.y + 50 + ScreenDimensions.h / 2) * 2 - 32 * ScalingParams.viewportY

                debug.clear()
                const lineMap = prunedSegments.reduce((acc, x) => {
                    acc.push(x.a)
                    acc.push(x.b)
                    return acc
                }, [])

                debug.beginFill(0xff0000, 0.95)
                for (let point of lineMap) {
                    debug.drawCircle(point.x, point.y, 3)
                }
                debug.endFill()

                debug.beginFill(0x00ff00, debug ? 0.2 : 1)
                for (let polygon of polygons) {
                    for (let point of polygon) {
                        debug.drawCircle(point.x, point.y, 3)
                    }
                }
                debug.endFill()

                debug.beginFill(0xff0000, 0.2)
                for (let polygon of polygons) {
                    debug.drawPolygon(flatten(polygon))
                }
                debug.endFill()
            }

            if (!veiled) {
                g.beginFill(0xff0000, sightAlpha)
                for (let polygon of polygons) {
                    g.drawPolygon(flatten(this.drawnUpHill(polygon)))
                }
                g.endFill()
            }
        } else if (fadingOcclusion && !veiled) {
            g.beginFill(0xff0000, sightAlpha)
            g.drawPolygon(flatten(this.drawnUpHill(this.outgoingSight)))
            g.endFill()
        }

        // Veiled, the light layer is the outdoor lighting everywhere and the veil does the hiding.
        const ambient = darkness ?? 1
        if (veiled) {
            // A building's sight polygon is also kept as the outgoing one, so this is the live
            // sightline indoors and the one fading out on the way through the door.
            const sightline = this.outgoingSight
            this.drawSightVeil(sightline, occlusionStrength * (1 - HiddenBrightness), sight, location, wallArt, intactArt)
        }

        if (veiled || darkness !== undefined) {
            g.beginFill(0xfffffff, veiled ? ambient : backgroundAlpha)
            // for now -- hardcode to large map size
            g.drawRect(
                0,
                0,
                this.logic.tilemapMetaProvider.width() * TileSize,
                this.logic.tilemapMetaProvider.height() * TileSize,
            )
            g.endFill()
        }

        if ((veiled || darkness !== undefined) && lights) {
            lights.iterate(light => {
                const {
                    location: { x, y: groundY },
                    intensity,
                    radius,
                } = light
                // Drawn where its source is drawn, up a hill (hill_lift.ts): by the ground under the source's feet, not
                // under its location, which at a cliff's foot is up on the top. Cast against the ground it stands on.
                const y = groundY - (light.lift ?? bodyLiftAt(this.logic.tilemapMetaProvider, x, groundY))
                if (!this.lightCanReachView(playerLocation, x, y, radius)) {
                    return
                }
                // A sight-gated light (an organic cave's crystals) counts only while the player can see
                // where it comes from: lights are shaped against the rock, not against the player's view.
                if (light.sightGated && (!hasPolymap || !this.sightPolygon || !insidePolygon(this.sightPolygon, x, groundY))) {
                    return
                }
                // `falloff` rings: the same shape drawn smaller and smaller, each over the last, so the
                // light is brightest at its heart. Each ring's alpha is picked so they stack to `intensity`.
                const rings = Math.max(1, Math.floor(light.falloff ?? 1))
                const alpha = rings > 1 ? 1 - Math.pow(1 - intensity, 1 / rings) : intensity
                let polygon: Array<{ x: number; y: number }> | undefined
                if (hasPolymap && visibility?.fullOcclusion) {
                    // A light must stop at the same cave rock as the player's sight. Drawing an
                    // unrestricted circle here paints over the shadow polygon drawn above.
                    // Its reach is pulled in by the same reveal the polygon is pushed out by,
                    // so the light still reaches exactly `radius` across open ground.
                    const reach = Math.max(1, radius - WallFaceReveal)
                    // Include long walls crossing the light even if both endpoints lie outside it.
                    const blockers = polyMap.slice(4).filter(({ a, b }) =>
                        Math.min(a.x, b.x) <= x + radius && Math.max(a.x, b.x) >= x - radius &&
                        Math.min(a.y, b.y) <= groundY + radius && Math.max(a.y, b.y) >= groundY - radius)
                    polygon = getLightPolygon(blockers, x, groundY, reach, WallFaceReveal, circleSides(reach))
                    if (y !== groundY) polygon = polygon.map(point => ({ ...point, y: point.y - (groundY - y) }))
                }
                for (let ring = 0; ring < rings; ring++) {
                    const scale = (rings - ring) / rings
                    g.beginFill(0xffffff, alpha)
                    if (polygon) {
                        g.drawPolygon(flatten(ring === 0 ? polygon : clampToRadius(polygon, x, y, radius * scale)))
                    } else {
                        g.drawCircle(x, y, radius * scale)
                    }
                    g.endFill()
                }
            })
        }

        this.lastLocation.x = location.x
        this.lastLocation.y = location.y

        // NEW: Mark that texture needs update and update cached hash
        this.needsTextureUpdate = true
        this.cachedVisibilityHash = currentVisibilityHash

        if (this.debugHashCalculation) {
            logger.debug(
                { tags: ["shadow", "cache", "miss"] },
                `[Shadow Cache] Cache MISS for hash: ${currentVisibilityHash}`,
            )
        }

        return true
    }

    /**
     * Whether a light could put any brightness inside the baked region, so the ones that cannot
     * are skipped before their sight polygon is cast. Nothing used to cull here at all: every
     * light in the room was shaped against its occluders and drawn, including campfires tens of
     * tiles away contributing not one pixel. That makes the per-frame cost scale with how many
     * lights the ROOM holds rather than how many are on screen.
     *
     * The bound is deliberately loose -- a full viewport of slack in each direction on top of the
     * half that is actually visible -- because the camera is free to sit up to half a viewport off
     * the player near a map edge (see the modifier clamping above), and a light that pops out at
     * the edge of the screen is far worse than one shaded for nothing.
     */
    private lightCanReachView = (center: Coordinate, x: number, y: number, radius: number) => {
        const { viewportX, viewportY, viewportPaddingX, viewportPaddingY } = ScalingParams
        return (
            Math.abs(x - center.x) <= (viewportX * 2 + viewportPaddingX) * TileSize + radius &&
            Math.abs(y - center.y) <= (viewportY * 2 + viewportPaddingY) * TileSize + radius
        )
    }

    /**
     * Veil everything the walls hide. Black drawn over the mask only scales down what is under it,
     * so a light brightens what the player can see and nothing behind the walls. Drawn into the
     * blurred layer, a light's circle used to paint over the hidden ground and show it through them.
     */
    private drawSightVeil = (sightline: SightPoint[] | null, alpha: number, sight: typeof INDOOR_SIGHT,
        eye: Coordinate, wallArt?: (x: number, y: number) => Sprite[], intactArt?: (art: Sprite) => Texture) => {
        const veil = this.sightVeil
        const erase = this.sightErase
        veil.clear()
        erase.clear()
        for (const proxy of this.wallProxies) proxy.visible = false
        if (!(alpha > 0) || !sightline) return
        for (const layer of [veil, erase, this.wallErase]) {
            layer.position.copyFrom(this.visibilitySurface.position)
            layer.pivot.copyFrom(this.visibilitySurface.pivot)
        }
        if (this.veilFeather) this.veilFeather.blur = sight.feather
        this.veilSprite.filters = this.veilFeather && sight.feather > 0 ? [this.veilFeather] : []

        // The whole map and well past it, since the veil texture overhangs the screen.
        const width = this.logic.tilemapMetaProvider.width() * TileSize
        const height = this.logic.tilemapMetaProvider.height() * TileSize
        veil.beginFill(0x000000, alpha)
        veil.drawRect(-width, -height, width * 3, height * 3)
        veil.endFill()

        // Sight is cast on the ground; it is drawn up the hill the eye stands on, with the walls round it (their art, which
        // eraseSeenWalls copies, is drawn there already).
        const flat = flatten(sightline)
        const drawn = this.drawnUpHill(sightline)
        erase.beginFill(0xffffff, 1)
        erase.drawPolygon(drawn === sightline ? flat : flatten(drawn))
        erase.endFill()
        const fan = penumbraFan(drawn, sight.penumbra, this.penumbra)
        for (let i = 0; i < fan.length; i += 7) {
            erase.beginFill(0xffffff, fan[i + 6])
            erase.drawPolygon(fan[i], fan[i + 1], fan[i + 2], fan[i + 3], fan[i + 4], fan[i + 5])
            erase.endFill()
        }
        if (wallArt) this.eraseSeenWalls(flat, eye, wallArt, intactArt)
        this.veilActive = true
    }

    /**
     * A sightline cast on the ground, as drawn: up the hill the player stands on (hill_lift.ts), with the building round
     * them. The same points off a hill.
     */
    private drawnUpHill = <P extends Coordinate>(points: P[]): P[] => {
        const player = this.logic.playerEntity
        const lift = player ? hillLiftOf(player, this.logic.tilemapMetaProvider) : 0
        return lift ? points.map(point => ({ ...point, y: point.y - lift })) : points
    }

    // Lift the veil off the drawn art of every wall the sightline reaches into, top and all.
    private eraseSeenWalls = (flat: number[], eye: Coordinate, wallArt: (x: number, y: number) => Sprite[],
        intactArt?: (art: Sprite) => Texture) => {
        const provider = this.logic.tilemapMetaProvider
        const eyeX = Math.floor(eye.x / TileSize)
        const eyeY = Math.floor(eye.y / TileSize)
        const seen = new Set<Sprite>()
        let used = 0
        for (let y = eyeY - WallSearchTiles; y <= eyeY + WallSearchTiles; y++) {
            for (let x = eyeX - WallSearchTiles; x <= eyeX + WallSearchTiles; x++) {
                if (!provider.isOcclusionAt(x, y) || !sightReachesTile(flat, x, y)) continue
                for (const art of wallArt(x, y)) {
                    if (seen.has(art)) continue
                    const texture = intactArt?.(art) ?? art.texture
                    // A texture freed under a live sprite would crash the proxy's texture setter.
                    if (!texture?.baseTexture) continue
                    seen.add(art)
                    const proxy = this.wallProxies[used++] ?? this.addWallProxy()
                    proxy.texture = texture
                    proxy.anchor.copyFrom(art.anchor)
                    proxy.position.copyFrom(art.position)
                    proxy.scale.copyFrom(art.scale)
                    proxy.pivot.copyFrom(art.pivot)
                    proxy.skew.copyFrom(art.skew)
                    proxy.rotation = art.rotation
                    proxy.visible = true
                }
            }
        }
    }

    private addWallProxy = (): Sprite => {
        const proxy = new Sprite()
        proxy.blendMode = BLEND_MODES.ERASE
        this.wallErase.addChild(proxy)
        this.wallProxies.push(proxy)
        return proxy
    }

    private render = () => {
        const bounds = this.visibilityBounds
        // Minimum texture size is 1x1; 0x0 throws.
        const width = Math.max(1, Math.ceil(bounds.width))
        const height = Math.max(1, Math.ceil(bounds.height))

        // Compare against the size WE asked for, not the texture's own: BaseTexture rounds what it
        // is given, so a fractional bounds.width would never equal texture.width and the texture
        // would be thrown away and rebuilt every single frame -- the very churn this replaced.
        if (!this.visibilityRenderTexture || this.bakedWidth !== width || this.bakedHeight !== height) {
            this.visibilityRenderTexture?.destroy(true)
            this.veilTexture?.destroy(true)
            this.veilTexture = undefined
            // The mask is magnified about 3x onto the screen. Nearest-neighbour sampling, the
            // client's default for its pixel art, turned every soft edge into visible steps.
            this.visibilityRenderTexture = RenderTexture.create({ width, height, scaleMode: SCALE_MODES.LINEAR })
            this.bakedWidth = width
            this.bakedHeight = height
        }
        this.visibilityTexture = this.visibilityRenderTexture

        // Same framing generateTexture applied: translate the baked region's top-left to the
        // texture's origin, and let the surface update its own transform since it has no parent.
        this.bakeTransform.tx = -bounds.x
        this.bakeTransform.ty = -bounds.y
        this.app.renderer.render(this.visibilitySurface, {
            renderTexture: this.visibilityRenderTexture,
            // generateTexture could skip this, because its texture was always brand new. Reusing
            // one means last frame's mask is still in it.
            clear: true,
            transform: this.bakeTransform,
            skipUpdateTransform: false,
        })
        if (this.veilActive) {
            if (!this.veilTexture) {
                this.veilTexture = RenderTexture.create({ width: width + VeilOverhang * 2, height: height + VeilOverhang * 2 })
                this.veilSprite.texture = this.veilTexture
            }
            this.veilTransform.tx = VeilOverhang - bounds.x
            this.veilTransform.ty = VeilOverhang - bounds.y
            const veil = { renderTexture: this.veilTexture, transform: this.veilTransform, skipUpdateTransform: false }
            this.app.renderer.render(this.sightVeil, { ...veil, clear: true })
            this.app.renderer.render(this.sightErase, { ...veil, clear: false })
            this.app.renderer.render(this.wallErase, { ...veil, clear: false })
            this.veilSprite.position.set(bounds.x - VeilOverhang, bounds.y - VeilOverhang)
            this.app.renderer.render(this.veilSprite, {
                renderTexture: this.visibilityRenderTexture,
                clear: false,
                transform: this.bakeTransform,
                skipUpdateTransform: false,
            })
        }

        if (!this.visibilitySprite) {
            this.visibilitySprite = new Sprite(this.visibilityTexture)
        } else if (this.visibilitySprite.texture !== this.visibilityTexture) {
            this.visibilitySprite.texture = this.visibilityTexture
        }
        // Drawn straight after the world, multiplying it (LIGHT_MAP_BLEND), where a mask would have drawn the world twice.
        ensureLightMapBlend(this.app.renderer as Renderer)
        if (this.surface.mask === this.visibilitySprite) this.surface.mask = null
        this.visibilitySprite.blendMode = LIGHT_MAP_BLEND
        this.visibilitySprite.renderable = true

        this.visibilitySprite.x = bounds.x
        this.visibilitySprite.y = bounds.y

        // Directly after the world on the stage: it darkens the world and nothing drawn over it (the HUD, the overlays).
        const stage = this.app.stage
        if (this.visibilitySprite.parent !== stage) stage.addChild(this.visibilitySprite)
        this.visibilitySprite.zIndex = this.surface.zIndex
        if (this.surface.parent === stage) {
            const world = stage.getChildIndex(this.surface), now = stage.getChildIndex(this.visibilitySprite)
            const after = now > world ? world + 1 : world
            if (now !== after) stage.setChildIndex(this.visibilitySprite, after)
        }
    }

    // --- PiP (follow-cam) light mask ------------------------------------------------------------
    // The main mask (visibilitySprite above) is baked in the PLAYER's screen space, so reusing it for
    // the ember follow-cam — which frames a DIFFERENT entity — makes every light drift across the PiP
    // as the player walks. This builds a SECOND alpha mask, baked in the PiP's OWN screen space: a dim
    // base fill plus brighter "holes" at each light's world position, transformed by the same
    // (cx, cy, zoom) the PiP world render uses (see renderWorldToTexture). Because it shares that
    // framing, each light stays fixed over the world regardless of where the player is.
    //
    // Alignment: the returned sprite is used verbatim as `surface.mask` for the isolated PiP render.
    // It is never parented, so its worldTransform stays identity, which makes the SpriteMaskFilter map
    // mask-texture pixel (X,Y) → render-texture pixel (X,Y) 1:1 (same contract the main mask relies
    // on). We therefore bake the framing into the texture, not the sprite transform.
    private pipDarkness?: Graphics
    private pipMaskTexture?: RenderTexture
    private pipMaskSprite?: Sprite

    renderPipLightMask = (opts: {
        cx: number
        cy: number
        zoom: number
        texW: number
        texH: number
        resolution: number
        visibility?: Visibility
        lights?: LightsContainer
        /** The rain's cloud cover over what the PiP shows, as for the main view. */
        cloud?: number
    }): Sprite | undefined => {
        const { cx, cy, zoom, texW, texH, resolution, visibility, lights, cloud } = opts
        // The night look's floor, uncoloured: the PiP is masked, not drawn through the night filter.
        const darkness = nightLookFor(visibility, !!this.logic.dayPhase, cloud)?.level ?? visibility?.darkness
        // No darkness in this room → no mask (the PiP renders at full brightness, same as the world).
        if (darkness === undefined || darkness >= 1.0) {
            return undefined
        }

        if (!this.pipDarkness) {
            this.pipDarkness = new Graphics()
            // Soften the light edges, mirroring the main mask's blur (scaled down for the PiP zoom).
            this.pipDarkness.filters = [new filters.BlurFilter(10)]
        }
        if (
            !this.pipMaskTexture ||
            this.pipMaskTexture.width !== texW ||
            this.pipMaskTexture.height !== texH ||
            this.pipMaskTexture.baseTexture.resolution !== resolution
        ) {
            this.pipMaskTexture?.destroy(true)
            this.pipMaskTexture = RenderTexture.create({ width: texW, height: texH, resolution })
            this.pipMaskSprite = new Sprite(this.pipMaskTexture)
        }

        const g = this.pipDarkness
        g.clear()

        // Base dim fill over just the visible PiP region (a small margin covers the blur bleed at the
        // edges). Bounding it to the view keeps the blur-filter texture small instead of map-sized.
        const halfW = texW / zoom / 2
        const halfH = texH / zoom / 2
        const margin = 48
        g.beginFill(0xffffff, darkness)
        g.drawRect(cx - halfW - margin, cy - halfH - margin, (halfW + margin) * 2, (halfH + margin) * 2)
        g.endFill()

        // Brighter "holes" at each in-view light's world position (matches prepare()'s light pass).
        lights?.iterate(light => {
            const {
                location: { x, y },
                intensity,
                radius,
            } = light
            if (Math.abs(x - cx) > halfW + radius || Math.abs(y - cy) > halfH + radius) {
                return
            }
            g.beginFill(0xffffff, intensity)
            g.drawCircle(x, y, radius)
            g.endFill()
        })

        // Bake the world→PiP-texture framing into the graphics transform (identical to the surface
        // transform in renderWorldToTexture), so the mask lines up pixel-for-pixel with the world.
        g.position.set(texW / 2 - cx * zoom, texH / 2 - cy * zoom)
        g.scale.set(zoom)

        this.app.renderer.render(g, { renderTexture: this.pipMaskTexture, clear: true })

        return this.pipMaskSprite
    }

    // NEW: Compute a hash of the current visibility state
    private computeVisibilityHash(state: {
        polyMap: Array<VectorLine>
        location: Coordinate
        visibility: Visibility
        lights: LightsContainer
        playerLocationChanged: boolean
        lightsChanged: boolean
        darknessChanged: boolean
    }): string {
        const { polyMap, location, visibility, lights } = state

        // Create a hash based on key factors that affect visibility.
        //
        // The location goes in at FULL precision. It used to be quantized to a 32px tile, which is
        // wrong for a mask baked in SCREEN space: prepare() positions the graphics with the camera
        // modifier taken from this exact location, so two positions inside one tile bake to
        // different places. Quantizing them to the same hash froze the mask while the world went on
        // scrolling, and every light -- the player's own and every campfire's -- slid against the
        // ground by however far the camera had moved, then snapped back on the next real bake.
        //
        // It survived locally because the OTHER half of the hash is lights.lastUpdatedTs, and the
        // lights are re-registered by entity_rendering_manager.update(), whose only callers are the
        // server-message handlers. On localhost an update lands on essentially every frame, so the
        // hash changed anyway; at 50ms it lands on ~3 frames in 4, and the rest drew a stale mask.
        // Measured on production: up to 9px of slide per snap in ordinary walking (~20 CSS px at a
        // typical canvas scale), and 50px across a burst of late frames.
        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const visibilityKey = visibility?.lastUpdateTs?.toString() || "0"

        return `${locationKey}-${darknessKey}-${lightsKey}-${polyMapKey}-${visibilityKey}-${this.app.stage.x}-${this.app.stage.y}-${this.surface.position.x}-${this.surface.position.y}-${this.surface.pivot.x}-${this.surface.pivot.y}`
    }

    // Force the next frame to re-bake. The render texture is NOT destroyed: it is still bound to
    // the mask sprite, and freeing it would leave that sprite pointing at a dead texture -- the
    // "Cannot read properties of null (reading 'valid')" crash in Sprite's texture setter if any
    // early return gets between here and the next render(). It is reused in place instead, and
    // only ever destroyed when the baked region's size changes.
    private invalidateMask = () => {
        this.cachedVisibilityHash = ""
        this.needsTextureUpdate = true
    }

    // Public: force the mask to re-bake (resize, debugging).
    forceTextureUpdate = () => {
        this.invalidateMask()
    }

    // Vestigial, kept only so the shadow_* debug satellites that read it keep working; the
    // texture cache they were written to observe is gone, and so is its hit/miss distinction.
    // These and those three files should go together.
    getCacheStats = () => {
        return {
            cacheSize: this.visibilityRenderTexture ? 1 : 0,
            maxCacheSize: 1,
            lastVisibilityHash: this.cachedVisibilityHash,
            needsUpdate: this.needsTextureUpdate,
        }
    }

    // NEW: Debug method to log current state
    debugLog = () => {
        const stats = this.getCacheStats()
        logger.debug(
            { tags: ["shadow", "debug", "cache"] },
            `[Shadow Debug] Cache: ${stats.cacheSize}/${stats.maxCacheSize}, Needs Update: ${stats.needsUpdate}, Hash: ${stats.lastVisibilityHash}`,
        )
    }

    // NEW: Debug method to log hash calculation details
    debugHashCalculationLog = (state: any) => {
        const { polyMap, location, visibility, lights, playerLocationChanged, lightsChanged, darknessChanged } = state

        const locationKey = `${location.x}-${location.y}`
        const darknessKey = visibility?.darkness?.toString() || "1.0"
        const lightsKey = lights?.lastUpdatedTs?.toString() || "0"
        const polyMapKey = polyMap?.length?.toString() || "0"
        const changeFlags = `${playerLocationChanged ? "1" : "0"}${lightsChanged ? "1" : "0"}${
            darknessChanged ? "1" : "0"
        }`

        logger.debug(
            { tags: ["shadow", "debug", "hash"] },
            `[Shadow Hash Debug] Location: ${locationKey}, Darkness: ${darknessKey}, Lights: ${lightsKey}, PolyMap: ${polyMapKey}, Changes: ${changeFlags}`,
        )
    }
}
